import { IdentityLayout, IdentityHero } from "@speedycore/identity-ui";

const BULLETS = [
  "Effective: 14 August 2026",
  "One policy covers the entire SpeedyCore platform",
  "No algorithmic surveillance, no selling your data",
  "Australian Privacy Principles (APPs) apply — based in Queensland, Australia",
] as const;

export function PrivacyPage() {
  return (
    <IdentityLayout
      hero={
        <IdentityHero
          title="Speedy Systems Pty Ltd — Privacy Policy"
          subtitle="How Speedy Systems Pty Ltd collects, uses, stores, and protects your personal information across the SpeedyCore platform."
          bullets={BULLETS}
        />
      }
      form={
        <article
          style={{
            padding: "1.5rem 1.75rem",
            color: "#111827",
            lineHeight: 1.65,
            fontSize: "0.92rem",
          }}
        >
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: 0 }}>1. Who We Are & Jurisdiction</h2>
          <p>
            <strong>Speedy Systems Pty Ltd</strong> (ACN 152 029 000) ("we", "us", "our") is the data controller for
            all services under SpeedyCore Identity, Constructive OS, PluginFactory AI, and every white-label
            hub and application that redirects authentication through SpeedyCore.
            We are located in Queensland, Australia, and our practices comply with the
            <em> Privacy Act 1988 (Cth)</em> and the 13 <strong>Australian Privacy Principles (APPs)</strong>, not
            the EU General Data Protection Regulation (GDPR).
            Contact our privacy officer: <a href="mailto:privacy@speedycore.com.au" style={{ color: "#1e3a8a", textDecoration: "underline" }}>privacy@speedycore.com.au</a>.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>2. Information We Collect</h2>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li><strong>Account identity</strong> — email address, display name, hashed password (never plaintext), IP address used at registration and each acceptance event.</li>
            <li><strong>Profile & workspace</strong> — business name, ABN/ACN (if provided), workspace members, roles, subscriptions.</li>
            <li><strong>Content you create</strong> — construction plans (COPF files), documents, photos, voice notes, measurements, notes, and any data you upload.</li>
            <li><strong>Device & session</strong> — user agent, screen resolution, locale, approximate timezone, RS256-signed session tokens.</li>
            <li><strong>Minimal telemetry</strong> — error codes, account creation failures, SSO handoff outcomes. No behavioural tracking, no third-party ad networks, no algorithmic profiling.</li>
          </ul>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>3. How We Use It</h2>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li>To authenticate your SpeedyCore identity across all apps (one account, one acceptance).</li>
            <li>To operate, host, sync, and back up the content you create.</li>
            <li>To process payments and subscriptions via our PCI-DSS compliant provider.</li>
            <li>To email you critical security, legal, and account notices (transactional only — no marketing without explicit opt-in).</li>
            <li>To defend legal claims, enforce our Terms, and comply with lawful government requests (we will notify you where legally permitted).</li>
          </ul>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>4. What We Do NOT Do</h2>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li>We do <strong>not</strong> sell or rent your personal information to third parties.</li>
            <li>We do <strong>not</strong> train our own or third-party machine-learning models on your plans, documents, or content without a separate, signed, revocable opt-in.</li>
            <li>We do <strong>not</strong> run behavioural advertising networks or share data with ad brokers.</li>
            <li>We do <strong>not</strong> engage in algorithmic user scoring, shadow-banning, or manipulation-based "growth" tactics.</li>
          </ul>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>5. Storage & Security</h2>
          <p>
            Primary production infrastructure is hosted in Australia on the VPS at 154.26.158.157,
            reverse-proxied through Caddy with TLS 1.3. All sensitive databases are encrypted at rest;
            credentials are stored via Argon2id and RS256 JWTs with strict expiry.
            Cross-app auth uses OAuth2-style short-lived bearer tokens minted only after legal acceptance is verified.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>6. Sharing & Subprocessors</h2>
          <p>
            We only share personal information with: (a) subprocessors bound by written confidentiality obligations
            (email delivery, payment processing, domain/DNS, and infrastructure providers), each of which we have
            vetted for compliance with the Australian Privacy Principles; (b) your own designated workspace members
            on your explicit instruction; or (c) when compelled by valid, specific, lawful process — in which case
            we will, where legally permitted, give you reasonable prior notice to allow you to contest the request.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>7. Retention</h2>
          <p>
            Account identity records are retained for the life of your account plus 7 years after closure
            for accounting, legal, and audit purposes. Individual workspace content is deleted in full
            within 90 days of permanent workspace deletion unless retention is required by law.
            IP-address acceptance logs are retained for 12 months after the next higher-version acceptance event.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>8. Your Rights (Australian Privacy Principles — APPs)</h2>
          <p>Under the Privacy Act 1988 (Cth) and the Australian Privacy Principles, you may request:</p>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li>Access to — or a portable export of — your personal information (APP 12).</li>
            <li>Correction of inaccurate or incomplete personal information (APP 13).</li>
            <li>Erasure or de-identification of your personal information, where we are not required by law to retain it (APP 13.2).</li>
            <li>Restriction or objection to specific processing consistent with APPs 6–10.</li>
            <li>Withdrawal of any consents you have previously given for processing that relies solely on consent.</li>
            <li>Complaint handling in accordance with APP 1.5 and our internal complaints process (see §13 below).</li>
          </ul>
          <p>
            Submit requests from your SpeedyCore account-registered email to
            <a href="mailto:privacy@speedycore.com.au" style={{ color: "#1e3a8a", textDecoration: "underline" }}> privacy@speedycore.com.au</a>.
            We respond within 30 days (extendable to 60 days for complex requests, with notice).
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>9. Cross-Border Transfers</h2>
          <p>
            Your data is processed primarily in Australia. Backup and disaster-recovery may transit New Zealand and
            Singapore endpoints; each transit destination has been assessed under our internal transfer impact
            assessment as providing substantially equivalent protection, and transfers are covered by our
            subprocessor agreements.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>10. Cookies & Identity</h2>
          <p>
            SpeedyCore Identity uses first-party, essential-only cookies strictly to maintain your authenticated
            session on SpeedyCore-owned domains. No third-party tracking cookies are set by us. Individual apps on
            non-speedycore.com.au domains receive bearer tokens via URL hash handoff and do not share session cookies.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>11. Children / Minimum Age — 18+ Only</h2>
          <p>
            SpeedyCore is intended for adults only. You must be at least <strong>18 years of age</strong> (or the
            applicable age of majority in your jurisdiction, whichever is higher) to register, use, or submit
            personal information to SpeedyCore. We do <em>not</em> knowingly collect personal information from any
            person under the age of 18. No account may be opened by, or used on behalf of, a minor; no parental,
            guardian, or carer-consent pathway is offered or honoured. If you believe an under‑18 person has
            registered or submitted personal information, contact us immediately at
            privacy@speedycore.com.au and we will expeditiously terminate the account and remove all associated data.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>12. Versioning & Changes</h2>
          <p>
            Material changes to this Privacy Policy will be accompanied by a bumped legal version in SpeedyCore,
            and you will be prompted to re-accept before any further app access is granted.
            Minor, non-prejudicial clarifications (typos, formatting, updated contact details) may be rolled
            out without requiring re-acceptance; the version number on this page will always be authoritative.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>13. Complaints (APP 1.5)</h2>
          <p>
            If you are unhappy with our handling of personal information, raise it with us first at
            <a href="mailto:privacy@speedycore.com.au" style={{ color: "#1e3a8a", textDecoration: "underline" }}> privacy@speedycore.com.au</a>
            (we aim to acknowledge within 5 business days and resolve within 30 days).
            If our internal complaint process does not resolve the matter to your satisfaction, you may refer the
            complaint to the Office of the Australian Information Commissioner (OAIC) at
            <a href="https://www.oaic.gov.au/privacy/privacy-complaints" target="_blank" rel="noopener noreferrer" style={{ color: "#1e3a8a", textDecoration: "underline" }}> www.oaic.gov.au/privacy/privacy-complaints</a>
            or your local external dispute resolution scheme recognised under APP 1.5.
          </p>
        </article>
      }
    />
  );
}

export default PrivacyPage;
